import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, UntypedFormArray } from '@angular/forms';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { NgxMaskModule } from 'ngx-mask';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { environment } from '../../../../environments/environment';
import { UtilityService } from '../../../core/utility.service';
import { OnlyDigitsDirective } from '../../../shared/directives/only-digits.directive';
import { DropdownComponent } from '../../../shared/dropdown/dropdown.component';
import { CustomCurrencyPipe } from '../../../shared/pipes/currency.pipe';
import { VendorService } from '../../vendor.service';
import { ICloneSource, IConflictMark, ILookupCategory, ILookupExwPoint, ILookupManufacturer, ItemResponse } from '../item-write/item.models';
import { conflictFor } from '../item-write/rules/item-write-concurrency';
import { carryIncompleteTiers, categoryForMainChoice, editedItemFromForm, IEditLookups, seedEditForm } from '../item-write/rules/item-write-edit.rules';
import { CharCounterComponent, charCounterVisible } from '../item-write/char-counter/char-counter.component';
import { createItemWriteForm, DESCRIPTION_MAX_LENGTH } from '../item-write/rules/item-write-form.factory';
import { dqFieldError } from '../item-write/rules/item-write-validators';
import { bannerError, formErrorNames, rowInvalidMessage } from '../item-write/rules/item-write-messages';
import {
  assignableStatusOptions, automaticStatusOptions, availabilityForStatus, isManualReactivationPending, isReactivationPending,
  ISkuStatusOption, statusForAvailability
} from '../item-write/rules/item-write-status.rules';
import { applyWritability, mayArchive } from '../item-write/rules/item-write-writability.rules';
import { ItemEditStore } from '../items/item-edit.store';
import { IListingRow } from '../items/item-listing.models';
import {
  isRowLocked, ListingRole, reactivationHint, rowNotices, rowSellerLabel
} from '../items/listing-row.rules';
import { CONFLICT_NOTICE, FIX_FIRST, REVIEW_CHANGES, REVIEW_FIRST, ROW_SAVED } from '../item-write/rules/item-write-messages';
import { manufacturerLogoPath } from '../items/manufacturer-logo';

/** Every lookup a listing row seeds and renders through: the category tree and the three
 *  attributes (as the modal seeds), plus the manufacturers (the new control, and the logo)
 *  and the ExW points (the expanded view). Loaded once by the page from /lookups. */
export interface IListingRowLookups extends IEditLookups {
  manufacturers: ILookupManufacturer[];
  exwPoints: ILookupExwPoint[];
}

/**
 * The inline listing row (HUBX-8480), on the v2 row and the shared write rules.
 *
 * The row renders `IListingRow` and keeps NO state of its own beyond its form. Its edited
 * state lives in the page's ItemEditStore (HUBX-8479): the form is built here from
 * `store.current(id)` through the one factory (HUBX-8474) - on every (re)build, which is
 * what lets the inline row and the expanded view swap without losing an edit - and every
 * change is folded back into the store as a complete ItemResponse (editedItemFromForm). An
 * edit changed back to what was stored leaves the store, so "edited" always means "differs".
 *
 * The page bumps `revision` when the store moved this row behind the form (undo all, a save,
 * the refill, the Removed tab's bulk cost cut) and the form is rebuilt from the store.
 *
 * What it no longer does, because the popup's rules now answer it (the row and the popup
 * "disagree on no rule"): the status list is the popup's two groups plus writableFields, not the
 * six legacy status lookups filtered by role and tab; what may be written is applyWritability,
 * not seller names or a private VendorManager portfolio; validity is the shared validators,
 * not itemService.validateForm. Legacy's Seller cost cap, the operator's cost-to-price copy
 * and the Seller's "Quantity Discounts removed" popup have no counterpart in those rules and
 * are gone with them (decided for HUBX-8480): raising a cost is allowed and says so
 * (the re-review notice), and a Seller is not sent discount tiers at all.
 */
@Component({
  selector: 'app-item-simple',
  templateUrl: './item-simple.component.html',
  styleUrls: ['./item-simple.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, NgFor, NgClass, FormsModule, ReactiveFormsModule, TooltipModule, DropdownComponent, OnlyDigitsDirective, NgxMaskModule, DatePipe, CustomCurrencyPipe, CharCounterComponent]
})
export class ItemSimpleComponent implements OnChanges, OnDestroy {
  @Input() row!: IListingRow;
  @Input() store!: ItemEditStore;
  @Input() lookups!: IListingRowLookups;
  /** ADMIN / VENDORMGR: the form factory's role gate (sale pricing, XID). */
  @Input() pricing = false;
  @Input() role: ListingRole = 'SELLER';
  /** Company -> owning VendorManager, for the tooltip on a vendor outside the VM's scope. */
  @Input() vmOwners?: Map<string, string>;
  /** A global search: the Part Number tooltip names the company. */
  @Input() isAll = false;
  /** The Edit modal is open on this row. */
  @Input() selected = false;
  @Input() revision = 0;
  /** The last save's failure for this row, already worded by the shared catalogue. */
  @Input() saveError: string | null = null;
  /** The From Quantity of the discount tier the last save's failure names (the cost-margin
   *  400, HUBX-8466), or null. Held by the page, with the line it came with. */
  @Input() saveErrorDq: number | null = null;
  /** A 412 on this row's last save (HUBX-8478): the members to ring, empty when the 412
   *  carried no currentState. Null when there is no conflict. Held by the page. While it is
   *  set the row cannot be saved: it is resolved in the resolve popup first (HUBX-8466). */
  @Input() conflict: Map<string, IConflictMark> | null = null;
  /** The server refused this row's last save over its data (a 400, HUBX-8466), and the row has
   *  not been edited since: sent again as it is, it would be refused again, so Save waits for
   *  an edit. The line under the row says why (saveError). Held by the page. */
  @Input() refused = false;
  /** The last save wrote this row (HUBX-8478); the page fades it. */
  @Input() justSaved = false;
  /** What the saved mark says instead of "Saved." (HUBX-8466): the item was approved or
   *  reactivated and stays Paused by the vendor. Held by the page, which knows what was sent. */
  @Input() savedNote: string | null = null;
  @Input() reactivationSelectable = false;
  @Input() reactivationChecked = false;

  @Output() editDetailedMode = new EventEmitter<IListingRow>();
  /** Clone (HUBX-8368 / HUBX-8422): the identity and the header fields the Create modal shows. */
  @Output() cloneItem = new EventEmitter<ICloneSource>();
  /** The row's own Save icon: the page saves this row alone. */
  @Output() singleItemUpdated = new EventEmitter<string>();
  /** The row's entry in the store changed (typed, undone, archived). */
  @Output() edited = new EventEmitter<string>();
  @Output() reactivationToggled = new EventEmitter<{ id: string; checked: boolean }>();
  /** Review changes on the conflict notice (HUBX-8466): the page re-reads this row and opens
   *  the resolve popup. */
  @Output() conflictReview = new EventEmitter<string>();

  readonly imageBaseUrl = environment.imageBaseUrl;
  readonly reviewLabel = REVIEW_CHANGES;
  readonly reviewFirst = REVIEW_FIRST;
  customPatterns = {
    '1': { pattern: new RegExp('[1-9]') },
    '0': { pattern: new RegExp('[0-9]') }
  };

  fg!: FormGroup;
  subCategories: ILookupCategory[] = [];
  /** The Edit modal's status select, group for group: "You can set", then "Automatic"
   *  (disabled). Whether the control is enabled at all is writableFields' call. */
  assignableStatuses: ISkuStatusOption[] = [];
  automaticStatuses: ISkuStatusOption[] = [];
  locked = false;
  sellerLabel = '';
  tooltipMsg = 'Archived';

  private formSubscription = new Subscription();
  private seeding = false;

  constructor(
    protected fb: FormBuilder,
    protected cdr: ChangeDetectorRef,
    protected utilityService: UtilityService,
    public vendorService: VendorService
  ) { }

  get id(): string {
    return this.row.item.id;
  }

  /** The item as stored: what the rules compare with, and whose writableFields decide. */
  get baseline(): ItemResponse {
    return this.store?.baseline(this.id) ?? this.row.item;
  }

  /** What the row shows: its edit, or the stored item. */
  get current(): ItemResponse {
    return this.store?.current(this.id) ?? this.row.item;
  }

  get isEdited(): boolean {
    return !!this.store?.isEdited(this.id);
  }

  get isArchived(): boolean {
    return this.current.isArchived;
  }

  get mayArchive(): boolean {
    return !this.locked && mayArchive(this.baseline.writableFields ?? []);
  }

  get dqControls(): UntypedFormArray {
    return this.fg.get('discountQtys') as UntypedFormArray;
  }

  /** DQ #1-3 as the Edit modal draws them, on both listing views (HUBX-8466): all three slots
   *  for ADMIN / VM, empty ones included, so a tier can be set from the row at any time; none
   *  for a Seller, who may neither read nor set them. The role decides, not the payload. */
  get dqSlots(): FormGroup[] {
    return this.pricing ? this.dqControls.controls as FormGroup[] : [];
  }

  /** Approval / re-review / reactivation / manual reactivation: the popup's notices, same
   *  rules, same words. The form goes in whole for the re-review: ETA, MOQ and MXQ are the
   *  row's to edit too, and the expanded row's Special Notes and Restrictions (HUBX-8515). */
  get notices(): string[] {
    return rowNotices(this.fg.controls['skuStatus'].value, this.fg.controls['cost'].value, this.baseline, this.row.reactivationThreshold, this.role,
      this.current.isArchived, this.fg.getRawValue());
  }

  /** The standing Removed-row line, until a notice already says what Save does to the Removed
   *  state: the typed cost reactivates it, or an operator's Active does (HUBX-8476). Shown
   *  beside the manual one, "reduce cost" would contradict "will reactivate anyway". */
  get reactivationHint(): string | null {
    // The rules, not the words: a notice may carry the stays-Paused sentence (HUBX-8466).
    const status = this.fg.controls['skuStatus'].value;
    const cost = this.fg.controls['cost'].value;
    const writable = this.baseline.writableFields ?? [];
    const threshold = this.row.reactivationThreshold;
    const reactivating = isReactivationPending(cost, this.baseline, writable, threshold)
      || isManualReactivationPending(status, cost, this.baseline, writable, threshold, this.role !== 'SELLER');
    return reactivating ? null : reactivationHint(this.row);
  }

  /** Legacy's reduce-cost popup on Cost (2026-10-07): offered while the standing Removed line
   *  stands and this caller may write Cost - on a locked row, an archived one or one whose
   *  cost is not in writableFields, "Reduce to" could not act. */
  get showReduceCost(): boolean {
    return !!this.reactivationHint && this.fg.controls['cost'].enabled;
  }

  /** The cut the popup names, as the standing line words it (the server's band percent). */
  get reductionPercent(): number {
    return Number(this.row.reactivationReductionPercent);
  }

  /** "Reduce to": the row's threshold as its cost - the Removed tab's bulk cut, for this row -
   *  through the form, so the store, the notices and Undo follow exactly as for typing. The
   *  reactivation notice then replaces the standing line, and the popup goes with it. */
  applyReactivationThreshold(): void {
    const threshold = this.row.reactivationThreshold;
    if (threshold == null || !this.showReduceCost) { return; }
    const cost = this.fg.controls['cost'];
    cost.setValue(threshold);
    cost.markAsDirty();
  }

  /** The line under the row: the shared banner's specific messages (a touched cross-field or
   *  discount rule), else - on an edited row - what the form refuses, as the Edit modal says
   *  it (rowInvalidMessage: the ringed fields, plus by name any this view does not draw), else
   *  the last save's failure. A refused row is never sent, so the page's pre-flight copy of
   *  the same refusal gives way to this live line, which knows what this view draws. */
  get errorLine(): string | null {
    const specific = bannerError(this.fg, this.dqControls, null, false);
    if (specific) { return specific; }
    if (this.isEdited && this.fg.invalid) {
      return rowInvalidMessage(formErrorNames(this.fg), new Set(this.visibleControls()), this.dqControls);
    }
    return this.saveError ?? null;
  }

  get savedText(): string {
    return this.savedNote ?? ROW_SAVED;
  }

  /** The Description counter (shown on focus, and while over the limit). The limit is the
   *  form's validator; the line under the row says why a long text is refused. */
  readonly descriptionMax = DESCRIPTION_MAX_LENGTH;
  descriptionFocused = false;

  get descriptionLength(): number {
    return String(this.fg?.controls['description']?.value ?? '').length;
  }

  get showDescriptionCounter(): boolean {
    return charCounterVisible(this.descriptionFocused, this.descriptionLength, this.descriptionMax);
  }

  /** The success mark, in the slot the error occupies. It is only an acknowledgement, so an
   *  error line or a conflict notice on the same row takes the slot instead. */
  get showSaved(): boolean {
    return this.justSaved && !this.conflictNotice && !this.errorLine;
  }

  /** The Edit modal's amber notice, while this row's last save came back 412. */
  get conflictNotice(): string | null {
    return this.conflict ? CONFLICT_NOTICE : null;
  }

  /** Whether the band under the row holds any line. Empty, it drops its top gap, so a plain
   *  row ends closer to its selects (2026-10-07). */
  get hasBanner(): boolean {
    return !!this.reactivationHint || this.notices.length > 0 || !!this.conflictNotice || !!this.errorLine || this.showSaved;
  }

  /** The Edit modal's amber ring on a member both writers moved. The category pair carries
   *  one mark, `categoryId`, ringed on both selects as the modal does. */
  conflictOpen(control: string): boolean {
    if (!this.conflict) { return false; }
    return !!conflictFor(this.conflict, control === 'categoryMainId' ? 'categoryId' : control);
  }

  reviewConflict(): void {
    this.conflictReview.emit(this.id);
  }

  /** The row's Save icon is live: an edit the form accepts, no conflict left to resolve, and
   *  no server refusal of this very edit (HUBX-8466). */
  get canSave(): boolean {
    return this.isEdited && !this.fg.invalid && !this.conflict && !this.refused;
  }

  /** What the Save icon says (HUBX-8466): why it waits, else what it does. A conflict first,
   *  as the page's Update All words it: resolving one can change the values to fix. */
  get saveTooltip(): string {
    if (this.conflict) { return REVIEW_FIRST; }
    return this.isEdited && (this.fg.invalid || this.refused) ? FIX_FIRST : 'Save';
  }

  /** The Edit modal's red ring on a refused field, once the row is edited (an untouched row
   *  with incomplete stored data says nothing, as before). A missing category leaf with no sub
   *  list to show rings the main select, which is where it is chosen. */
  showInvalid(name: string): boolean {
    if (!this.fg || !this.isEdited) { return false; }
    if (name === 'categoryMainId' && !this.subCategories.length && this.fg.controls['categoryId'].invalid) {
      return true;
    }
    return !!this.fg.controls[name]?.invalid;
  }

  /** The ring on a discount-tier input (HUBX-8480): the input at fault, as in the modal. Shown
   *  once the row is edited, or once the tier is typed in - the line under the row names it
   *  then (bannerError, on a touched or dirty tier), and the ring goes with the line. A
   *  half-typed tier makes the row edited on its own (carryIncompleteTiers, HUBX-8466). */
  showDqInvalid(tier: FormGroup, field: 'fromQty' | 'price'): boolean {
    if (field === 'price' && this.saveError && this.saveErrorDq !== null
      && Number(tier.controls['fromQty'].value) === this.saveErrorDq) {
      return true;
    }
    return (this.isEdited || tier.touched || tier.dirty) && dqFieldError(tier, field);
  }

  /** The controls this view draws, so rowInvalidMessage knows which refusals are ringed and
   *  which must be named. The simple row has no attribute band, no ExW and no MPN. ETA and MXQ
   *  for every role (2026-10-07); Price for ADMIN / VM only. */
  protected visibleControls(): string[] {
    return [
      'vendorItemCode', 'description', 'manufacturerId', 'categoryMainId', 'categoryId', 'availability', 'leadTimeDays',
      'moq', 'mxq', 'cost',
      ...(this.pricing ? ['unitPrice'] : [])
    ];
  }

  // Select options on demand. A page of rows drew every option of every select up front -
  // 25 rows x 605 manufacturers was ~15,000 <option> elements, 85% of the page's DOM, before
  // anyone opened one. Each select now draws only its chosen option until it is focused or
  // pressed (openOptions), then its whole list for good. Both events come before the native
  // list opens and before type-ahead, so neither sees a short list. The forms themselves stay
  // eager: building 25 costs ~23ms.

  /** The selects of this row whose whole list has been drawn, by control name. */
  private readonly openedSelects = new Set<string>();

  /** What a select draws: the whole list once opened; until then only the chosen option (none
   *  for an empty or unknown value - the placeholder shows). Values compare as strings, as a
   *  select's do (the ExW point is a number in the form, a string in the option). */
  optionsFor<T, K extends keyof T>(control: string, all: readonly T[] | null | undefined, key: K): readonly T[] {
    const list = all ?? [];
    if (this.openedSelects.has(control)) { return list; }
    const value = this.fg?.controls[control]?.value;
    if (value === null || value === undefined || value === '') { return []; }
    const chosen = list.find(o => String(o[key]) === String(value));
    return chosen ? [chosen] : [];
  }

  openOptions(control: string): void {
    if (this.openedSelects.has(control)) { return; }
    this.openedSelects.add(control);
    this.cdr.markForCheck();
  }

  /** The chosen manufacturer's name, as the select's tooltip: the control is narrow and long
   *  names are cut. */
  get manufacturerName(): string {
    const id = this.fg?.controls['manufacturerId'].value;
    return this.lookups?.manufacturers.find(m => m.id === id)?.name ?? '';
  }

  get manufacturerLink(): string {
    const path = manufacturerLogoPath(this.lookups?.manufacturers, this.fg?.controls['manufacturerId'].value);
    return path || '/assets/images/item-placeholder.svg';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['row'] || changes['store'] || changes['lookups'] || changes['revision'] || changes['pricing']) {
      if (this.row && this.store && this.lookups) {
        this.build();
      }
    }
    if (changes['role'] || changes['vmOwners'] || changes['row']) {
      if (this.row) {
        this.sellerLabel = rowSellerLabel(this.role, this.row, this.vmOwners);
      }
    }
  }

  ngOnDestroy(): void {
    this.formSubscription.unsubscribe();
  }

  /** Build the row's form from the store (see the class comment). Seeding is not an edit:
   *  nothing reaches the store until the form settles. */
  protected build(): void {
    this.formSubscription.unsubscribe();
    this.formSubscription = new Subscription();

    const fg = createItemWriteForm(this.fb, this.pricing);
    this.seeding = true;
    this.subCategories = seedEditForm(fg, this.current, this.lookups) ?? [];
    this.locked = isRowLocked(this.vendorService.isPortalReadOnlyVendor, this.baseline.writableFields ?? []);
    if (this.locked || this.current.isArchived) {
      // A read-only portal, a row the server gave nothing to write, or an item the user
      // archived (Restore rebuilds it) - the whole row is inert, as legacy's archived row.
      fg.disable({ emitEvent: false });
    } else {
      applyWritability(fg, this.baseline.writableFields ?? []);
    }
    fg.updateValueAndValidity({ emitEvent: false });
    this.seeding = false;

    this.fg = fg;
    this.assignableStatuses = assignableStatusOptions(this.baseline);
    this.automaticStatuses = automaticStatusOptions(this.baseline);

    // Status <-> Available: one fact behind two controls (shared rule). Written silently, so
    // the form settles once and the store receives both halves together.
    this.formSubscription.add(fg.controls['skuStatus'].valueChanges.subscribe(next => {
      const change = availabilityForStatus(next, this.numberOrNull(fg.controls['availability'].value), this.baseline);
      if (change) {
        fg.controls['availability'].setValue(change.availability, { emitEvent: false });
      }
    }));
    this.formSubscription.add(fg.controls['availability'].valueChanges.subscribe(value => {
      const next = statusForAvailability(value, fg.controls['skuStatus'].value);
      if (next) {
        fg.controls['skuStatus'].setValue(next, { emitEvent: false });
      }
    }));
    // The category pair, now live: Main chains Sub exactly as the popup does.
    this.formSubscription.add(fg.controls['categoryMainId'].valueChanges.subscribe(mainId => {
      const choice = categoryForMainChoice(this.lookups.categories, mainId);
      this.subCategories = choice.subCategories;
      fg.controls['categoryId'].setValue(choice.categoryId, { emitEvent: false });
    }));
    this.formSubscription.add(fg.valueChanges.subscribe(() => this.commit()));
  }

  /** Fold the form into the store: the row's full state, or no entry when it matches what
   *  was stored. A half-typed discount tier is carried too, and is an edit on its own
   *  (HUBX-8466): the save refuses the row rather than dropping the tier, and the row counts
   *  as unsaved while it shows the tier refused (carryIncompleteTiers). */
  protected commit(): void {
    if (this.seeding) { return; }
    const v = this.fg.getRawValue();
    this.store.set(this.id, carryIncompleteTiers(editedItemFromForm(v, this.current, this.lookups), v));
    this.edited.emit(this.id);
    this.cdr.markForCheck();
  }

  /** Undo restores from memory (HUBX-8480): the store drops the entry and the form is
   *  rebuilt from the stored item. No request - legacy issued getVendorItemById per row.
   *  On a conflicted row the stored item is the stale one, so the page then re-reads the row
   *  (onRowEdited): Undo there is Discard my changes (HUBX-8466). */
  undoItemUpdates(e?: Event): void {
    e?.stopPropagation();
    if (this.locked || !this.isEdited) { return; }
    this.store.undo(this.id);
    this.build();
    this.edited.emit(this.id);
  }

  updateSingleItem(): void {
    if (this.locked || !this.canSave) { return; }
    if (this.vendorService.isPortalReadOnly(true)) { return; }
    this.singleItemUpdated.emit(this.id);
  }

  /** Archive / Restore set the archive INTENT on the row's edit (HUBX-8438): a write member
   *  of the PUT, not a status. Offered only when the server grants `isArchived`. */
  archiveItem(): void {
    this.setArchived(true);
  }

  restoreItem(): void {
    this.setArchived(false);
  }

  private setArchived(isArchived: boolean): void {
    if (!this.mayArchive || this.vendorService.isPortalReadOnly(true)) { return; }
    this.store.edit(this.id, { isArchived });
    this.build();
    this.edited.emit(this.id);
  }

  openEditDetailedMode(): void {
    // The logo is the one control a locked row leaves live, so the read-only check is here.
    if (this.vendorService.isPortalReadOnly(true)) { return; }
    this.editDetailedMode.emit(this.row);
  }

  /** Clone entry point. The company is named for operators only - a Seller sees one company,
   *  and naming it in the header would be noise. A locked row still clones: Clone writes
   *  nothing on the row (legacy parity). The page stops a read-only portal with its popup;
   *  a row outside the caller's write scope seeds a Create the caller may make. */
  triggerClone(e: Event): void {
    e.stopPropagation();
    this.cloneItem.emit({
      id: this.id,
      vendorItemCode: this.row.item.vendorItemCode,
      companyName: this.role === 'SELLER' ? null : this.row.companyName,
      launchedOn: this.row.lastActivatedOn,
      updatedOn: this.row.lastUpdatedOn
    });
  }

  toggleReactivation(e: Event): void {
    this.reactivationToggled.emit({ id: this.id, checked: (e.target as HTMLInputElement).checked });
  }

  copyText(val: string | null | undefined): void {
    if (val) { this.utilityService.copyTextToClipboard(val); }
  }

  /** Removes the mask prefix a masked input leaves behind when emptied (legacy behaviour). */
  checkPrefix(elementRef: HTMLInputElement): void {
    if (elementRef.value && elementRef.value.length === 1) {
      elementRef.value = '';
    }
  }

  private numberOrNull(value: unknown): number | null {
    return value == null || value === '' ? null : Number(value);
  }
}
